'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { getCategories, type ApiCategory } from '@/lib/api';

const FALLBACK_CATEGORY_IMAGES = [
  '/assets/images/categories/food.jpg',
  '/assets/images/categories/beverages.jpg',
  '/assets/images/categories/chemicals.jpg',
  '/assets/images/categories/cosmetics.jpg',
  '/assets/images/categories/stationery.jpg',
  '/assets/images/categories/textiles.jpg',
];

function fallbackImageForSlug(slug: string): string {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h + slug.charCodeAt(i) * (i + 1)) % 997;
  return FALLBACK_CATEGORY_IMAGES[h % FALLBACK_CATEGORY_IMAGES.length];
}

interface DisplayCategory {
  id: string;
  name: string;
  slug: string;
  image: string;
  productCount: number;
}

interface CategoryQuickAccessProps {
  variant?: 'grid' | 'dropdown';
  maxVisible?: number;
  className?: string;
}

function buildDisplayCategories(apiCategories: ApiCategory[]): DisplayCategory[] {
  return apiCategories.map((c) => ({
    id: String(c.id),
    name: c.name,
    slug: c.slug,
    image: (c.coverImage && c.coverImage.trim()) || fallbackImageForSlug(c.slug),
    productCount: c.productCount ?? 0,
  }));
}

const CategoryQuickAccess = ({
  variant = 'grid',
  maxVisible = 6,
  className = '',
}: CategoryQuickAccessProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<DisplayCategory[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getCategories()
      .then((cats) => {
        if (cancelled) return;
        setCategories(buildDisplayCategories(cats));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Не удалось загрузить категории'
        );
        setCategories([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleCategories = useMemo(
    () => categories.slice(0, maxVisible),
    [categories, maxVisible]
  );

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCategoryClick = () => {
    setIsDropdownOpen(false);
  };

  const gridSkeleton = (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      {Array.from({ length: maxVisible }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-md bg-card shadow-sm"
        >
          <div className="aspect-square animate-pulse bg-muted" />
          <div className="space-y-2 p-4">
            <div className="h-4 animate-pulse rounded bg-muted" />
            <div className="h-3 w-20 animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );

  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={handleDropdownToggle}
          className="flex items-center gap-2 rounded-md bg-card px-4 py-2 font-heading text-base font-medium text-text-primary shadow-sm transition-all duration-250 hover:bg-muted hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 active:scale-[0.97]"
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
        >
          <Icon name="Squares2X2Icon" size={20} />
          <span>Категории</span>
          <Icon
            name="ChevronDownIcon"
            size={16}
            className={`transition-transform duration-250 ${isDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-[150]"
              onClick={handleDropdownToggle}
              aria-hidden="true"
            />
            <div className="absolute left-0 top-full z-[150] mt-2 w-80 rounded-md bg-popover shadow-lg">
              <div className="max-h-96 overflow-y-auto p-2">
                {loading && (
                  <p className="p-3 font-caption text-sm text-text-secondary">
                    Загрузка…
                  </p>
                )}
                {error && (
                  <p className="p-3 font-caption text-sm text-error">{error}</p>
                )}
                {!loading &&
                  !error &&
                  categories.map((category) => (
                    <Link
                      key={category.id}
                      href={`/product-catalog?category=${encodeURIComponent(category.slug)}`}
                      onClick={handleCategoryClick}
                      className="flex items-center gap-3 rounded-md p-3 transition-all duration-250 hover:bg-muted active:scale-[0.97]"
                    >
                      <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md">
                        <AppImage
                          src={category.image}
                          alt={category.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate font-heading text-sm font-medium text-text-primary">
                          {category.name}
                        </h4>
                        <p className="font-caption text-xs text-text-secondary">
                          {category.productCount} товаров
                        </p>
                      </div>
                      <Icon
                        name="ChevronRightIcon"
                        size={16}
                        className="text-text-secondary"
                      />
                    </Link>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-heading text-2xl font-semibold text-text-primary">
          Категории товаров
        </h2>
        <Link
          href="/product-catalog"
          className="flex items-center gap-1 font-heading text-sm font-medium text-accent transition-colors duration-250 hover:text-accent/80 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
        >
          <span>Все категории</span>
          <Icon name="ArrowRightIcon" size={16} />
        </Link>
      </div>

      {error && (
        <p className="mb-4 font-body text-sm text-error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        gridSkeleton
      ) : visibleCategories.length === 0 ? (
        <p className="font-body text-sm text-text-secondary">
          Пока нет категорий в каталоге.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {visibleCategories.map((category) => (
            <Link
              key={category.id}
              href={`/product-catalog?category=${encodeURIComponent(category.slug)}`}
              className="group flex flex-col overflow-hidden rounded-md bg-card shadow-sm transition-all duration-250 hover:-translate-y-1 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
            >
              <div className="relative aspect-square overflow-hidden">
                <AppImage
                  src={category.image}
                  alt={category.name}
                  className="h-full w-full object-cover transition-transform duration-250 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent opacity-0 transition-opacity duration-250 group-hover:opacity-100" />
              </div>
              <div className="p-4">
                <h3 className="mb-1 line-clamp-2 font-heading text-sm font-medium text-text-primary">
                  {category.name}
                </h3>
                <p className="font-caption text-xs text-text-secondary">
                  {category.productCount} товаров
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryQuickAccess;
